import { Code2, Columns2, MessageSquare, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';

import './field-workbench.css';

import type { CSSProperties, ReactNode } from 'react';

type View = 'split' | 'company' | 'editor';
const LAYOUT_KEY = 'mio.field.layout.v1';
const NAV_MIN = 192;
const NAV_MAX = 320;
const NAV_DEFAULT = 224;
const NAV_RAIL = 64;
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

function readLayout() {
  const fallback = { width: NAV_DEFAULT, collapsed: false, company: 44 };
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(LAYOUT_KEY) ?? 'null');
    if (!saved || typeof saved !== 'object') return fallback;
    const { width, collapsed, company } = saved as Record<string, unknown>;
    return {
      width:
        typeof width === 'number' && Number.isFinite(width)
          ? clamp(width, NAV_MIN, NAV_MAX)
          : fallback.width,
      collapsed: collapsed === true,
      company:
        typeof company === 'number' && Number.isFinite(company)
          ? clamp(company, 32, 65)
          : fallback.company,
    };
  } catch {
    return fallback;
  }
}

const views = [
  { id: 'company', label: 'Company only', text: 'Project', icon: MessageSquare },
  { id: 'split', label: 'Code and company', text: 'Split', icon: Columns2 },
  { id: 'editor', label: 'Editor only', text: 'Code', icon: Code2 },
] as const;

/** Layout changes keep the editor and drafts mounted. Only layout preferences are stored. */
export function FieldWorkbench({
  navigation,
  children,
  renderEditor,
  visible,
  title = 'Project',
  onCompanyVisibilityChange,
  editorRequest = 0,
  companyRequest = 0,
  guide,
  onExploreEditor,
}: {
  navigation: (
    showCompany: () => void,
    options: { collapsed: boolean; onExpand: () => void },
  ) => ReactNode;
  children: ReactNode | ((controls: { openEditor: () => void }) => ReactNode);
  title?: string;
  onCompanyVisibilityChange?: (visible: boolean) => void;
  editorRequest?: number;
  companyRequest?: number;
  guide?: ReactNode;
  onExploreEditor?: () => void;
  renderEditor: (options: { visible: boolean; onClose: () => void }) => ReactNode;
  visible: boolean;
}) {
  const [view, setView] = useState<View>('company');
  const [layout, setLayout] = useState(readLayout);
  const [resizing, setResizing] = useState<'navigation' | 'split' | null>(null);
  const drag = useRef<'navigation' | 'split' | null>(null);
  const [wide, setWide] = useState(true);
  const [compact, setCompact] = useState(false);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [bodyHeight, setBodyHeight] = useState(0);
  const container = useRef<HTMLDivElement>(null);
  const companyButton = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const navigationId = useId();
  useEffect(() => {
    if (editorRequest) setView('editor');
  }, [editorRequest]);
  useEffect(() => {
    if (companyRequest) setView('company');
  }, [companyRequest]);
  const effectiveView = view === 'split' && !wide ? 'company' : view;
  const collapsed = layout.collapsed && !compact;
  const navigationWidth = collapsed ? NAV_RAIL : layout.width;
  const splitSpace = bodyWidth >= 760 ? bodyWidth - 8 : 0;
  const splitMin = splitSpace ? Math.max(32, Math.ceil((280 / splitSpace) * 100)) : 32;
  const splitMax = splitSpace ? Math.min(65, Math.floor((1 - 280 / splitSpace) * 100)) : 65;
  const companyWidth = clamp(layout.company, splitMin, splitMax);
  const expand = () => setLayout((previous) => ({ ...previous, collapsed: false }));
  const toggle = () => setLayout((previous) => ({ ...previous, collapsed: !previous.collapsed }));
  const stopResize = () => {
    drag.current = null;
    setResizing(null);
  };
  const resize = (value: number) =>
    setLayout((previous) => ({ ...previous, company: clamp(value, splitMin, splitMax) }));
  const resizeNavigation = (value: number) =>
    setLayout((previous) =>
      value < 128
        ? { ...previous, collapsed: true }
        : { ...previous, collapsed: false, width: clamp(Math.round(value), NAV_MIN, NAV_MAX) },
    );

  useEffect(() => {
    onCompanyVisibilityChange?.(visible && effectiveView !== 'editor');
  }, [visible, effectiveView, onCompanyVisibilityChange]);
  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
    } catch {
      /* In-memory resizing still works when storage is disabled. */
    }
  }, [layout]);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const update = () => {
      const width = element.getBoundingClientRect().width;
      if (width <= 0) return;
      const pane = body.current?.getBoundingClientRect();
      const availableWidth = pane && pane.width > 0 ? pane.width : width - navigationWidth - 8;
      setCompact(width <= 740);
      setWide(width > 740 && availableWidth >= 760);
      setBodyWidth(pane?.width ?? 0);
      setBodyHeight(pane?.height ?? 0);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    if (body.current) observer.observe(body.current);
    return () => observer.disconnect();
  }, [navigationWidth]);

  return (
    <div
      ref={container}
      className="field-workbench"
      data-view={effectiveView}
      data-navigation-collapsed={collapsed || undefined}
      data-resizing={resizing ?? undefined}
      style={
        {
          '--field-navigation-width': `${navigationWidth}px`,
          '--field-search-max-height': `${bodyHeight + 28}px`,
        } as CSSProperties
      }
    >
      <aside id={navigationId} className="field-explorer" aria-label="Delivery engagement panel">
        <Tooltip label={collapsed ? 'Expand delivery panel' : 'Collapse delivery panel'}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="field-navigation-toggle panel-icon-control"
            aria-label={collapsed ? 'Expand delivery panel' : 'Collapse delivery panel'}
            aria-controls={navigationId}
            aria-expanded={!collapsed}
            onClick={toggle}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} strokeWidth={1.7} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} strokeWidth={1.7} aria-hidden="true" />
            )}
          </Button>
        </Tooltip>
        {navigation(() => setView((current) => (current === 'editor' ? 'company' : current)), {
          collapsed,
          onExpand: expand,
        })}
      </aside>
      <div
        className="field-navigation-divider"
        role="separator"
        tabIndex={0}
        aria-label="Resize delivery engagement panel"
        aria-controls={navigationId}
        aria-orientation="vertical"
        aria-valuemin={NAV_RAIL}
        aria-valuemax={NAV_MAX}
        aria-valuenow={navigationWidth}
        aria-valuetext={
          collapsed ? 'Collapsed to navigation icons' : `Delivery panel ${navigationWidth} pixels`
        }
        title="Drag to resize or collapse. Double-click to reset."
        onDoubleClick={() =>
          setLayout((previous) => ({ ...previous, width: NAV_DEFAULT, collapsed: false }))
        }
        onKeyDown={(event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Enter') toggle();
          else if (
            event.key === 'Home' ||
            (event.key === 'ArrowLeft' && navigationWidth <= NAV_MIN)
          )
            setLayout((previous) => ({ ...previous, collapsed: true }));
          else if (event.key === 'End') resizeNavigation(NAV_MAX);
          else if (event.key === 'ArrowRight' && collapsed) expand();
          else resizeNavigation(navigationWidth + (event.key === 'ArrowRight' ? 16 : -16));
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.focus();
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = 'navigation';
          setResizing('navigation');
        }}
        onPointerMove={(event) => {
          if (drag.current !== 'navigation' || !container.current) return;
          resizeNavigation(event.clientX - container.current.getBoundingClientRect().left);
        }}
        onPointerUp={stopResize}
        onPointerCancel={stopResize}
        onLostPointerCapture={stopResize}
      >
        <span className="panel-resize-grip" aria-hidden="true" />
      </div>
      <div className="field-workbench-main">
        <div className="field-workbench-toolbar">
          <div className="field-location-group">
            <span className="field-location">{title}</span>
          </div>
          <div className="field-view-controls" role="group" aria-label="Workspace layout">
            {views.map(({ id, label, text, icon: Icon }) => (
              <Button
                key={id}
                ref={id === 'company' ? companyButton : undefined}
                type="button"
                variant="ghost"
                size="sm"
                aria-label={label}
                aria-pressed={effectiveView === id}
                title={label}
                onClick={() => {
                  if (id !== 'company') onExploreEditor?.();
                  setView(id);
                }}
                className={cn(
                  'field-view-control',
                  id === 'split' && !wide && 'field-split-unavailable',
                )}
              >
                <Icon className="size-3.5" aria-hidden="true" />
                <span>{text}</span>
              </Button>
            ))}
          </div>
        </div>
        {guide}
        <div
          ref={body}
          className="field-workbench-body"
          style={
            {
              '--field-company-share': `${companyWidth}fr`,
              '--field-editor-share': `${100 - companyWidth}fr`,
            } as CSSProperties
          }
        >
          <div className="field-editor-pane" inert={effectiveView === 'company' || !visible}>
            {renderEditor({
              visible: visible && effectiveView !== 'company',
              onClose: () => {
                setView('company');
                companyButton.current?.focus();
              },
            })}
          </div>
          <div
            className="field-workbench-divider"
            role="separator"
            tabIndex={0}
            aria-label="Resize editor and company panels"
            aria-orientation="vertical"
            aria-valuemin={splitMin}
            aria-valuemax={splitMax}
            aria-valuenow={companyWidth}
            aria-valuetext={`Company panel ${companyWidth} percent`}
            title="Drag to resize. Double-click to reset."
            onDoubleClick={() => resize(44)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
                resize(companyWidth + (event.key === 'ArrowLeft' ? 3 : -3));
              } else if (event.key === 'Home' || event.key === 'End') {
                event.preventDefault();
                resize(event.key === 'Home' ? splitMin : splitMax);
              }
            }}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              event.currentTarget.focus();
              event.currentTarget.setPointerCapture(event.pointerId);
              drag.current = 'split';
              setResizing('split');
            }}
            onPointerMove={(event) => {
              if (drag.current !== 'split' || !body.current) return;
              const bounds = body.current.getBoundingClientRect();
              if (bounds.width > 8)
                resize(Math.round(((bounds.right - event.clientX - 4) / (bounds.width - 8)) * 100));
            }}
            onPointerUp={stopResize}
            onPointerCancel={stopResize}
            onLostPointerCapture={stopResize}
          >
            <span className="panel-resize-grip" aria-hidden="true" />
          </div>
          <div className="field-company-pane" inert={effectiveView === 'editor' || !visible}>
            {typeof children === 'function'
              ? children({
                  openEditor: () => {
                    onExploreEditor?.();
                    setView(wide ? 'split' : 'editor');
                  },
                })
              : children}
          </div>
        </div>
      </div>
    </div>
  );
}
